export interface FaqEntry {
  id: string;
  question: string;
  answer: string;
}

export interface FaqCategory {
  id: string;
  title: string;
  entries: FaqEntry[];
}

export const weekendBookletFaqData: FaqCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    entries: [
      {
        id: 'gs-1',
        question: 'What is the Weekend Booklet?',
        answer:
          'The Weekend Booklet is a commemorative publication produced for the KDU (Krio Descendants Union) family reunion weekend. It serves as a keepsake for attendees, containing welcome messages, event schedules, member profiles, sponsor advertisements, memorial tributes, photo galleries, committee reports, and general articles. The Weekend Booklet Committee manages its production through this system, coordinating content from submission through to printed copies available for sale at the reunion.',
      },
      {
        id: 'gs-2',
        question: 'What are the four main tabs and what do they do?',
        answer:
          'The Weekend Booklet module has four tabs: (1) Submissions — where you create, manage, and review all content submissions for the booklet including articles, ads, and photos; (2) Layout — where you assign page numbers to approved content and organize the physical structure of the booklet; (3) Print & Sales — where you manage print orders with vendors, set pricing, and record booklet sales; (4) Dashboard — where you monitor overall production progress including submission status counts, contributor statistics, deadline countdowns, and print order status.',
      },
      {
        id: 'gs-3',
        question: 'Who has access to the Weekend Booklet module?',
        answer:
          'The Weekend Booklet module is accessible to users with the Organizer role. Within the module, access to specific features depends on your sub-committee membership and role. Members of the Weekend Booklet Committee can view submissions, review content, and participate in the editorial process. The Chair of the Weekend Booklet Committee has additional privileges including assigning reviewers, managing committee membership, and creating tasks.',
      },
      {
        id: 'gs-4',
        question: 'What is the overall production workflow?',
        answer:
          'The booklet production follows this workflow: First, an edition is created with a name, year, submission deadline, and target page count. Contributors then submit content which enters the editorial review pipeline (Draft → Submitted → UnderReview → RevisionRequested → Approved → ReadyForLayout). Once content is approved, it moves to the Layout tab where page numbers are assigned. After layout is complete, print orders are placed with vendors. Finally, when printed copies arrive, pricing is set and sales are recorded. The Dashboard tab provides real-time visibility into progress at every stage.',
      },
    ],
  },
  {
    id: 'edition-management',
    title: 'Edition Management',
    entries: [
      {
        id: 'em-1',
        question: 'What is a booklet edition and when should I create one?',
        answer:
          'A booklet edition represents a single publication for a specific reunion event — for example, "Georgia 2026 Weekend Booklet." You should create a new edition when planning begins for an upcoming reunion. Each edition tracks its own submissions, layout, print orders, pricing, and sales independently. Typically you will have one active edition at a time corresponding to the next upcoming reunion.',
      },
      {
        id: 'em-2',
        question: 'How do I create a new edition?',
        answer:
          'To create a new edition, navigate to the Submissions tab and click "Create Edition." You will need to provide: a name (e.g., "Georgia 2026 Weekend Booklet"), an optional year, a submission deadline (the date by which all content must be submitted), and an optional target page count for layout planning. Once created, the edition is automatically activated so submissions can begin immediately.',
      },
      {
        id: 'em-3',
        question: 'What is an "active edition" and how do I activate one?',
        answer:
          'The active edition is the one currently being worked on — all new submissions, layout assignments, and sales are associated with it. Only one edition can be active at a time. When you create a new edition it is automatically activated. If you need to switch back to a previous edition (for example, to record late sales for a past reunion booklet), you can activate it from the edition list. Activating an edition does not affect any data in other editions.',
      },
      {
        id: 'em-4',
        question: 'How do I update edition details after creation?',
        answer:
          'To update an edition, click the "Edit Edition" button in the active edition overview on the Submissions tab. You can modify the name, year, submission deadline, and target page count. This is useful when deadlines need to be extended or when the committee decides to adjust the target page count based on the volume of submissions received. Changes take effect immediately.',
      },
    ],
  },
  {
    id: 'content-submission',
    title: 'Content Submission',
    entries: [
      {
        id: 'cs-1',
        question: 'How do I create a new submission?',
        answer:
          'To create a new submission, go to the Submissions tab and click "New Submission." You must provide a title, author name, and section category. Optionally, you can include body text and an attachment URL. For sponsor ad submissions, you will also select the sponsor and ad size. The submission starts in Draft status and can be edited before being submitted for review.',
      },
      {
        id: 'cs-2',
        question: 'What are the section categories and what do they mean?',
        answer:
          'Section categories organize booklet content by type: WelcomeMessage — opening letters from family leaders or committee chairs; EventSchedule — the reunion weekend itinerary and activity times; MemberProfiles — biographical features on family members; SponsorAds — paid advertisements from sponsors supporting the reunion; MemorialTributes — remembrances for deceased family members; PhotoGallery — collections of family photos past and present; CommitteeReports — updates from reunion subcommittees; GeneralArticles — any other content such as recipes, poems, or family history pieces.',
      },
      {
        id: 'cs-3',
        question: 'How do sponsor ad submissions work?',
        answer:
          'When creating a submission with the SponsorAds section category, you can associate it with a specific sponsor from the system and select an ad size: FullPage, HalfPage, or QuarterPage. The ad size determines how much space the advertisement will occupy in the final booklet layout. Sponsor ads follow the same editorial review workflow as other content and must be approved before being assigned a page number.',
      },
      {
        id: 'cs-4',
        question: 'How are attachments and photo submissions handled?',
        answer:
          'Each submission can include an attachment URL field where you can link to uploaded images, documents, or other files. This is particularly useful for PhotoGallery submissions and SponsorAds where visual assets are essential. The attachment URL should point to the hosted location of the file. Multiple photos for a gallery can be submitted as separate submissions or referenced through a single shared album link.',
      },
      {
        id: 'cs-5',
        question: 'How does version tracking work for submissions?',
        answer:
          'The system automatically tracks versions of each submission. Every time a submission is saved with changes, a new version is recorded with a version number, the title, body content, and a timestamp. You can view the version history for any submission to see how it has evolved over time. This is especially useful when revisions are requested — you can compare the current version with previous ones to see what changed.',
      },
    ],
  },
  {
    id: 'editorial-review',
    title: 'Editorial Review',
    entries: [
      {
        id: 'er-1',
        question: 'What is the editorial workflow and what are the status transitions?',
        answer:
          'The editorial workflow moves submissions through these statuses: Draft (initial creation, still being prepared) → Submitted (ready for committee review) → UnderReview (actively being evaluated by a reviewer) → RevisionRequested (feedback given, author needs to make changes) → Approved (content accepted for the booklet) → ReadyForLayout (approved and ready to be assigned a page number). Each transition represents a deliberate action by an Organizer moving the submission forward or back in the process.',
      },
      {
        id: 'er-2',
        question: 'What does each status mean and what actions are available?',
        answer:
          'Draft — the submission is being prepared and can be edited freely; action: Submit for review. Submitted — waiting to be picked up for review; action: Move to UnderReview. UnderReview — a reviewer is evaluating the content; actions: Approve or Request Revision. RevisionRequested — the author has been asked to make changes; action: Resubmit (moves back to Submitted). Approved — content is accepted; action: Mark as ReadyForLayout. ReadyForLayout — content is finalized and waiting for page assignment; no further status changes needed.',
      },
      {
        id: 'er-3',
        question: 'How do I transition a submission between statuses?',
        answer:
          'To change a submission status, click on the submission in the Submissions tab to expand its details. You will see a status change dropdown showing only the valid next statuses based on the current state. Select the target status and confirm. The system enforces valid transitions — for example, you cannot jump directly from Draft to Approved. Each status change is logged with a timestamp for audit purposes.',
      },
      {
        id: 'er-4',
        question: 'How do review comments work?',
        answer:
          'Review comments allow Organizers to provide feedback on submissions. To add a comment, expand the submission details and use the comment form at the bottom. Each comment includes the commenter name, the comment text, and a timestamp. Comments are visible to all Organizers and serve as a record of editorial feedback, revision requests, and approval notes. They are especially useful when requesting revisions to explain what changes are needed.',
      },
      {
        id: 'er-5',
        question: 'What happens when a revision is requested?',
        answer:
          'When a submission is moved to RevisionRequested status, it signals that the content needs changes before it can be approved. The reviewer should add a comment explaining what revisions are needed. The author (or an Organizer on their behalf) then makes the necessary edits to the submission content. Once changes are complete, the submission is moved back to Submitted status, which re-enters it into the review queue. A new version is recorded so the reviewer can compare the updated content against the previous version.',
      },
    ],
  },
  {
    id: 'reviewer-assignment',
    title: 'Reviewer Assignment',
    entries: [
      {
        id: 'ra-1',
        question: 'How do I assign a reviewer to a submission?',
        answer:
          'To assign a reviewer, expand the submission details in the Submissions tab and look for the reviewer assignment section. A dropdown menu shows all members of the Weekend Booklet Committee by name. Select the person you want to assign and click "Assign Reviewer." You can assign multiple reviewers to a single submission by repeating this process — each time you assign someone, they are removed from the dropdown to prevent duplicates.',
      },
      {
        id: 'ra-2',
        question: 'How do I remove a reviewer from a submission?',
        answer:
          'To remove a reviewer, expand the submission details and look at the list of assigned reviewers. Each reviewer entry has a "Remove" button that will unassign them from that submission. Once removed, the reviewer reappears in the assignment dropdown so they can be reassigned if needed. Removing a reviewer does not delete any comments they may have already left — those remain part of the submission history.',
      },
      {
        id: 'ra-3',
        question: 'Who appears in the reviewer dropdown?',
        answer:
          'The reviewer dropdown is populated with members who are affiliated with the Weekend Booklet sub-committee. It shows each member\'s first and last name for easy identification. Members who are already assigned as reviewers for that specific submission are automatically excluded from the dropdown to prevent duplicate assignments. If all committee members are already assigned, the dropdown shows "All members assigned" in a disabled state.',
      },
    ],
  },
  {
    id: 'page-layout',
    title: 'Page Layout',
    entries: [
      {
        id: 'pl-1',
        question: 'What does the Layout tab do and when should I use it?',
        answer:
          'The Layout tab is where you organize the physical structure of the booklet by assigning page numbers to approved content. Use it once you have submissions that have been approved or marked as ReadyForLayout. The Layout tab shows two lists: content that already has page numbers assigned (allocated) and content that is eligible but not yet assigned (unallocated). This is typically one of the later steps in production, after most content has been through editorial review.',
      },
      {
        id: 'pl-2',
        question: 'How do I assign page numbers to approved submissions?',
        answer:
          'In the Layout tab, find the submission in the unallocated list and enter the desired page number in the input field next to it. Click "Assign" to save the page number. The submission will move from the unallocated list to the allocated list, sorted by page number. You can update page numbers later if the layout needs to be reorganized.',
      },
      {
        id: 'pl-3',
        question: 'Which submissions are eligible for page assignment?',
        answer:
          'Only submissions with a status of Approved or ReadyForLayout are eligible for page number assignment. Submissions still in Draft, Submitted, UnderReview, or RevisionRequested status must complete the editorial review process before they can appear in the Layout tab. This ensures only finalized, committee-approved content makes it into the booklet layout.',
      },
      {
        id: 'pl-4',
        question: 'How does the target page count relate to layout planning?',
        answer:
          'The target page count set on the edition serves as a planning guide for the layout process. It helps the committee understand how much content they need and how to allocate space. For example, if the target is 48 pages and you have 60 pages worth of approved content, you may need to adjust ad sizes or trim articles. The target page count is displayed in the edition overview and can be updated as planning evolves.',
      },
    ],
  },
  {
    id: 'print-ordering',
    title: 'Print Ordering',
    entries: [
      {
        id: 'po-1',
        question: 'How do I create a print order?',
        answer:
          'To create a print order, go to the Print & Sales tab and click "New Print Order." You must provide: the quantity of copies to print, the printer vendor name (the company doing the printing), the total cost of the order, the order date, and an expected delivery date. This information helps the committee track production costs and plan for when booklets will be available for sale at the reunion.',
      },
      {
        id: 'po-2',
        question: 'What are the print order statuses and how do I mark delivery?',
        answer:
          'Print orders have two statuses: Ordered (the order has been placed with the vendor) and Delivered (the printed copies have been received). When your print order arrives, update its status to Delivered. This updates the inventory metrics so the system knows how many copies are available for sale. The delivered date is recorded for tracking purposes.',
      },
      {
        id: 'po-3',
        question: 'Can I have multiple print orders for one edition?',
        answer:
          'Yes, you can create multiple print orders for a single edition. This is common when you need an initial run for the reunion event and then a second run if demand exceeds the first printing. Each order tracks its own quantity, cost, vendor, and delivery status independently. The total inventory (total printed) is calculated by summing the quantities from all delivered orders for the edition.',
      },
    ],
  },
  {
    id: 'pricing-sales',
    title: 'Pricing & Sales',
    entries: [
      {
        id: 'ps-1',
        question: 'How do I set the price per copy?',
        answer:
          'In the Print & Sales tab, use the pricing section to set the price per copy for the active edition. Enter the dollar amount and save. This price is used as the default when recording sales. You can update the price at any time — for example, you might offer a discounted price for pre-orders and then set a higher price at the reunion event itself.',
      },
      {
        id: 'ps-2',
        question: 'How do I record a booklet sale?',
        answer:
          'To record a sale, go to the Print & Sales tab and use the sale form. Enter the quantity of copies sold, the buyer name (optional but helpful for tracking), and select the payment method used. The revenue is calculated automatically based on the current price per copy multiplied by the quantity. Each sale is recorded with a timestamp and contributes to the revenue summary and inventory tracking.',
      },
      {
        id: 'ps-3',
        question: 'What payment methods are supported?',
        answer:
          'The system supports seven payment methods: Cash — physical currency; Check — personal or cashier\'s check; CreditCard — credit or debit card payment; BankTransfer — direct bank-to-bank transfer; PayPal — PayPal electronic payment; Zelle — Zelle instant transfer; Other — any payment method not listed above. Select the method that matches how the buyer paid when recording each sale.',
      },
      {
        id: 'ps-4',
        question: 'How is pricing history maintained when the price changes?',
        answer:
          'Every time you set or update the price per copy, a new pricing record is created with the amount and a timestamp. Previous pricing records are preserved, creating a complete history of price changes for the edition. This means sales recorded at different times may reflect different prices. The revenue calculations use the actual revenue recorded at the time of each sale, not the current price, ensuring accurate financial tracking.',
      },
    ],
  },
  {
    id: 'revenue-inventory',
    title: 'Revenue & Inventory Tracking',
    entries: [
      {
        id: 'ri-1',
        question: 'How does the revenue summary work?',
        answer:
          'The revenue summary provides a financial overview of booklet sales for the active edition. It shows: total revenue (the sum of all sale amounts), transaction count (how many individual sales have been recorded), and a breakdown by payment method showing the total amount and number of transactions for each method used. This helps the committee understand how much money has been collected and through which channels.',
      },
      {
        id: 'ri-2',
        question: 'How are inventory metrics calculated?',
        answer:
          'Inventory metrics are calculated from print orders and sales: Total Printed is the sum of quantities from all delivered print orders; Total Sold is the sum of quantities from all recorded sales; Remaining is Total Printed minus Total Sold. Note that only delivered print orders count toward the total printed — orders still in "Ordered" status are not included since those copies are not yet available. This gives you a real-time view of how many booklets are still available for sale.',
      },
      {
        id: 'ri-3',
        question: 'How do I interpret the revenue breakdown data?',
        answer:
          'The revenue breakdown groups sales by payment method, showing for each method: the total dollar amount collected and the number of transactions. This helps identify which payment methods are most popular with buyers and ensures all revenue streams are accounted for. For example, if you see 15 Cash transactions totaling $300 and 8 Zelle transactions totaling $160, you know cash is the preferred method and can plan accordingly for making change at the reunion.',
      },
    ],
  },
  {
    id: 'production-dashboard',
    title: 'Production Dashboard',
    entries: [
      {
        id: 'pd-1',
        question: 'What does the Dashboard tab show?',
        answer:
          'The Dashboard tab provides a high-level overview of the entire booklet production process for the active edition. It displays three revenue cards at the top (Booklet Sales, Booklet Spaces, and Sponsors), followed by production progress metrics, contributor statistics, deadline information, and print order status — all in one place. Use it to quickly assess how the booklet is progressing without needing to dig into individual submissions or orders.',
      },
      {
        id: 'pd-2',
        question: 'What production progress metrics are shown?',
        answer:
          'The Dashboard shows: status counts (how many submissions are in each status — Draft, Submitted, UnderReview, RevisionRequested, Approved, ReadyForLayout), total submissions (the overall count of all submissions for the edition), unique authors (how many different contributors have submitted content), deadline countdown (days remaining until the submission deadline), and print order status (whether orders are not yet placed, ordered, or delivered). Together these metrics tell you how much content you have, where it is in the pipeline, and whether you are on track.',
      },
      {
        id: 'pd-3',
        question: 'How is the deadline countdown calculated?',
        answer:
          'The deadline countdown is calculated as the number of days between today and the submission deadline set on the active edition. If the deadline is in the future, it shows the number of days remaining. If the deadline has passed, it indicates that the deadline is overdue. If the deadline is today, it shows "Due Today." This helps the committee know at a glance how much time contributors have left to submit their content.',
      },
      {
        id: 'pd-4',
        question: 'What are the three revenue cards on the Dashboard?',
        answer:
          'The Dashboard displays three revenue summary cards at the top: (1) Booklet Sales — total revenue from booklet copy sales and the number of transactions; (2) Booklet Spaces — combined revenue from Advertisers and Tile Space Purchasers who purchased space in the booklet; (3) Sponsors — the count of sponsor ad submissions in the current edition. These give a quick financial snapshot of the booklet\'s revenue streams.',
      },
    ],
  },
  {
    id: 'access-controls',
    title: 'Access Controls & Permissions',
    entries: [
      {
        id: 'ac-1',
        question: 'How do sub-committee access controls work?',
        answer:
          'The Sub-Committees page enforces membership-based access controls. You can see all sub-committee cards (showing name, description, member count, and task count), but you can only expand and view the details of sub-committees you are a member of. If you are not a member of a committee, you will see a lock icon (🔒) instead of the expand arrow. This ensures committee-specific information like tasks, member lists, and internal details are only visible to committee members.',
      },
      {
        id: 'ac-2',
        question: 'What special access do sub-committee Chairs have?',
        answer:
          'Sub-committee Chairs have two elevated privileges: (1) A Chair of any sub-committee can expand and view the details of ALL sub-committees, not just their own — this provides cross-committee visibility for leadership coordination. (2) Only the Chair of a specific sub-committee can add or remove members, create tasks, and manage events for that committee. Regular members can view the committee details but cannot make changes to membership or create new tasks.',
      },
      {
        id: 'ac-3',
        question: 'How do I know which committees I can access?',
        answer:
          'On the Sub-Committees page, committees you can expand show a ▼ arrow on the right side of the card header. Committees you cannot access show a 🔒 lock icon instead. The lock icon has a tooltip explaining that you must be a member of that committee to view details. If you need access to a committee, ask the Chair of that committee to add you as a member through the Members tab.',
      },
      {
        id: 'ac-4',
        question: 'What can regular committee members do vs. Chairs?',
        answer:
          'Regular committee members can: view the committee details (name, description, creation date), see the full member list with roles and assignment dates, view all tasks with their status and due dates, expand tasks to see descriptions and change task status. Chairs can do all of the above plus: add new members to the committee, remove existing members, create new tasks, and manage events. The "+ Add Member" and "+ Add Task" buttons are only visible to the Chair of that specific committee.',
      },
      {
        id: 'ac-5',
        question: 'How does the sub-committee filter on the Members page work?',
        answer:
          'The Members page includes an "All Sub-Committees" dropdown filter that lets you filter the member directory by sub-committee affiliation. Selecting a sub-committee shows only members who are affiliated with that committee. This is available to all users who can access the Members page and helps quickly identify who belongs to which committee without needing to drill into each sub-committee individually.',
      },
    ],
  },
  {
    id: 'advertisers-tile-space',
    title: 'Advertisers & Tile Space Purchasers',
    entries: [
      {
        id: 'at-1',
        question: 'What are Advertisers and Tile Space Purchasers?',
        answer:
          'Advertisers and Tile Space Purchasers are two revenue-generating section categories in the Weekend Booklet. Advertisers are external parties (individuals or organizations) that purchase advertising space in the booklet. Tile Space Purchasers are those who purchase tile space (typically memorial or recognition tiles). Both categories track the purchaser name, revenue amount, payment method, and payment date for each entry.',
      },
      {
        id: 'at-2',
        question: 'How do I create an Advertiser or Tile Space Purchaser entry?',
        answer:
          'To create an entry, go to the Submissions tab and click "New Submission." Fill in the title (description of the purchase), set the Author Name to the purchaser\'s name, and select either "Advertisers" or "TileSpacePurchasers" as the section category. Once you select one of these categories, additional revenue fields appear: Amount (required), Payment Method (required), Payment Date (required), Contact Email (optional), Contact Phone (optional), and Notes (optional). Submit the form to create both the submission and its associated revenue record.',
      },
      {
        id: 'at-3',
        question: 'How do I filter and view Advertiser or Tile Space entries?',
        answer:
          'Use the "Filter by Section" dropdown at the top of the Submissions tab. Select "Advertisers" or "Tile Space Purchasers" to filter the list to only those entries. When filtering by these categories, each entry shows a green revenue badge with the dollar amount. You can also see the total revenue for each category displayed as a badge next to the dropdown when that category is selected.',
      },
      {
        id: 'at-4',
        question: 'Where can I see the total revenue from Advertisers and Tile Space Purchasers?',
        answer:
          'Revenue from these categories is visible in two places: (1) The Dashboard tab shows a "Space Revenue" section with the grand total, plus individual totals and entry counts for Advertisers and Tile Space Purchasers separately. (2) The "Booklet Spaces" revenue card at the top of the Dashboard shows the combined total from both categories. Additionally, when you filter submissions by either category, the revenue badge next to the dropdown shows that category\'s total.',
      },
    ],
  },
  {
    id: 'articles-view',
    title: 'Articles View (Kanban & Table)',
    entries: [
      {
        id: 'av-1',
        question: 'What is the Articles tab and how does it differ from the Submissions tab?',
        answer:
          'The Articles tab provides a focused view of only GeneralArticles submissions, organized visually by booklet section. Unlike the Submissions tab which shows all submission types in a flat list, the Articles tab offers two display modes: a Kanban board (default) that groups articles into color-coded columns by booklet section, and a Table view with sortable columns. This makes it easier to see content distribution across sections at a glance.',
      },
      {
        id: 'av-2',
        question: 'How does the Kanban board work?',
        answer:
          'The Kanban board displays nine columns: "Unassigned" (for articles without a booklet section) plus the eight booklet sections (Front Matter, Feature Stories, Heritage & History, Faith & Culture, People & Community, Culture & Lifestyle, KDU Global Impact, Looking Forward). Each column has a distinct color-coded header and shows the article count. Articles appear as compact cards showing title, author, status badge, and Reference ID (if set). Columns scroll vertically when they contain many articles.',
      },
      {
        id: 'av-3',
        question: 'How do I switch between Kanban and Table view?',
        answer:
          'At the top of the Articles tab, you will see a toggle with two buttons: "Kanban" and "Table." Click the desired view to switch. The toggle defaults to Kanban view. Switching views does not reload data — it simply changes how the same articles are displayed. The Table view shows columns for Title, Author, Booklet Section, Reference ID, and Status.',
      },
      {
        id: 'av-4',
        question: 'How do I view full article details from the Kanban board?',
        answer:
          'Click on any article card in the Kanban view to open a detail popup modal. The modal displays the full submission information: title, author, status badge, booklet section, Reference ID (if set), the complete article body text (scrollable), word count, submission date, assigned reviewers, and comments. Close the modal by clicking the dark backdrop area or pressing the Escape key.',
      },
    ],
  },
  {
    id: 'memorial-tiles-tab',
    title: 'Memorial Tiles Tab',
    entries: [
      {
        id: 'mt-1',
        question: 'What is the Memorial Tiles tab?',
        answer:
          'The Memorial Tiles tab provides a dedicated view for Tile Space Purchaser submissions. Instead of a kanban layout, it displays tiles in a 3-column grid (3 columns by 5 rows per page), making it easy to visualize how memorial tiles will appear in the booklet. Each tile card shows the title, purchaser name, status badge, revenue amount (if recorded), and Reference ID (if set).',
      },
      {
        id: 'mt-2',
        question: 'How does pagination work in the Memorial Tiles tab?',
        answer:
          'The grid shows up to 15 tiles per page (3 columns × 5 rows). When you have more than 15 tile submissions, pagination controls appear at the bottom with Previous and Next buttons and a "Page X of Y" indicator. The Previous button is disabled on the first page and the Next button is disabled on the last page. If tiles are removed while you are on a later page, the view automatically adjusts to show the last valid page.',
      },
    ],
  },
  {
    id: 'ad-space-tab',
    title: 'Ad Space Tab',
    entries: [
      {
        id: 'as-1',
        question: 'What is the Ad Space tab?',
        answer:
          'The Ad Space tab provides a kanban-style view specifically for Advertiser submissions, grouped by ad size. It displays four columns: Unassigned (ads without a size set), Full Page, Half Page, and Quarter Page. Each column has a distinct color scheme and shows the count of ads in that size category. This helps the committee quickly see how ad space is distributed and identify unassigned ads that need sizing.',
      },
      {
        id: 'as-2',
        question: 'How are ads grouped into columns?',
        answer:
          'Ads are automatically placed into columns based on their adSize field: FullPage ads go in the "Full Page" column (purple header), HalfPage ads go in "Half Page" (cyan header), QuarterPage ads go in "Quarter Page" (gold header), and ads without an adSize value go in "Unassigned" (gray header). Each card shows the title, advertiser name, status badge, and Reference ID if set.',
      },
    ],
  },
  {
    id: 'reference-id',
    title: 'Reference ID',
    entries: [
      {
        id: 'ri-ref-1',
        question: 'What is the Reference ID field?',
        answer:
          'The Reference ID is an optional free-text field on every booklet submission that lets you associate an external identifier — such as an order number, invoice ID, or purchase reference — with the submission. This is particularly useful for Tile Space Purchasers and Advertisers where you want to link the booklet entry back to a payment or order in another system.',
      },
      {
        id: 'ri-ref-2',
        question: 'How do I add or edit a Reference ID?',
        answer:
          'You can set the Reference ID when creating a new submission (there is a "Reference ID" text input in the creation form). To add or change it on an existing submission, go to the Submissions tab, click the submission to expand it, click "Edit Submission," and enter the Reference ID in the field. Save your changes. To clear a Reference ID, simply delete the text and save — it will be removed.',
      },
      {
        id: 'ri-ref-3',
        question: 'Where is the Reference ID displayed?',
        answer:
          'When a submission has a Reference ID set, it appears as "Ref: [value]" in small muted text on: (1) Article cards in the Kanban view, (2) Memorial Tile cards in the grid, (3) Ad Space cards in the kanban columns, (4) the "Ref ID" column in the Articles table view, and (5) the article detail popup modal. If no Reference ID is set, nothing is displayed — the field is completely hidden when empty.',
      },
    ],
  },
  {
    id: 'meal-selection',
    title: 'Meal Selection',
    entries: [
      {
        id: 'ms-1',
        question: 'How are meals assigned to registrations?',
        answer:
          'Meals are linked to event registrations through a MealSelection record. Each registration can have exactly one meal selection. When a user registers for an event (like the Legacy Ball), they can choose from the available meal options. The system stores this as a one-to-one relationship: one Registration → one MealSelection → one MealOption.',
      },
      {
        id: 'ms-2',
        question: 'Can an Admin or Organizer select a meal for an attendee?',
        answer:
          'Yes. Admins and Organizers can select or change a meal for any attendee. Navigate to the Legacy Ball page, find the attendee\'s registration, and use the meal selection dropdown to assign a meal option. The system uses an upsert — if the attendee already has a meal selected, it will be updated to the new choice. If no meal was previously selected, a new selection is created.',
      },
      {
        id: 'ms-3',
        question: 'Can attendees select their own meal?',
        answer:
          'Yes. Attendees can select their own meal through the My Weekend portal. When viewing their registration details, they can choose from the available meal options. The meal selection endpoint is accessible to all authenticated users (Attendee role and above), so any logged-in user can select a meal for their own registration.',
      },
      {
        id: 'ms-4',
        question: 'What meal options are available?',
        answer:
          'Meal options are managed by Organizers through the Legacy Ball page. Each meal option has a name (e.g., "Grilled Chicken", "Pan-Seared Salmon", "Vegetarian Pasta"), a description, and an active/inactive flag. Only active meal options appear in the selection dropdown. Organizers can add, edit, or deactivate meal options as needed.',
      },
      {
        id: 'ms-5',
        question: 'Can a meal selection be changed after it is made?',
        answer:
          'Yes. Meal selections can be changed at any time before the event. Both the attendee (for their own registration) and Admins/Organizers (for any registration) can update the selection. The system overwrites the previous choice with the new one — there is no history of previous selections.',
      },
    ],
  },
];
